/** @jsx jsx */
import { useState, useMemo, useCallback } from "react";
import { EditOutlined, CheckOutlined } from "@ant-design/icons";
import { jsx } from "@emotion/react";
import { Card, Descriptions, Divider, Button, Modal, Form, Popconfirm } from "antd";
import { connect } from "dva";
import moment from "moment-timezone";
import { dateStringToStandardFormat } from "@/utils/dateFormatter";
import { ShipmentStatus, LoadStatus, DealerExchangeType, LocationType } from "@/utils/enum";
import { getUrlParams } from "@/utils/utils";
import ETAWindow from "@/components/ETAWindow";
import RemovePickupDropoffModal from "@/components/RemovePickupDropoffModal/RemovePickupDropoffModal";
import ActualForm from "@/pages/AdminShipments/AdminShipmentView/components/ActualForm/ActualForm";
import ConfirmDropOffForm from "@/pages/AdminShipments/AdminShipmentView/components/ConfirmDropOffForm/ConfirmDropOffForm";
import ConfirmPickupForm from "@/pages/AdminShipments/AdminShipmentView/components/ConfirmPickupForm/ConfirmPickupForm";
import ETAForm from "@/pages/AdminShipments/AdminShipmentView/components/ETAForm/ETAForm";

const DeliveryTimes = ({ dispatch, adminShipmentView, exchangeShipmentStatus, configPerm }) => {
  const [deliveryForm] = Form.useForm();
  const [confirmPickupForm] = Form.useForm();
  const [confirmDropOffForm] = Form.useForm();
  const [actualForm] = Form.useForm();

  const [editPickupDeliveryVisible, setEditPickupDeliveryVisible] = useState(false);

  const [isLoadingDelivery, setIsLoadingDelivery] = useState(false);

  const [editActualDeliveryVisible, setEditActualDeliveryVisible] = useState(false);

  const [confirmPickupDealerExchangePopConfirmVisible, setConfirmPickupDealerExchangePopConfirmVisible] =
    useState(false);

  const [lotExitPopConfirmVisible, setLotExitPopConfirmVisible] = useState(false);

  const [editConfirmPickupVisible, setEditConfirmPickupVisible] = useState(false);

  const [isLoadingConfirmPickup, setIsLoadingConfirmPickup] = useState(false);

  const [editConfirmDropOffVisible, setEditConfirmDropOffVisible] = useState(false);

  const [isLoadingConfirmDropOff, setIsLoadingConfirmDropOff] = useState(false);

  const [isLoadingActualDelivery, setIsLoadingActualDelivery] = useState(false);

  const adminShipmentData = useMemo(() => adminShipmentView?.data, [adminShipmentView]);

  const clickEditPickupDelivery = useCallback(() => {
    setEditPickupDeliveryVisible(true);
  }, []);

  const clickEditActualDelivery = useCallback(() => {
    setEditActualDeliveryVisible(true);
  }, []);

  const cancelPickupDeliveryEdit = useCallback(() => {
    setEditPickupDeliveryVisible(false);
    deliveryForm?.resetFields();
    deliveryForm?.validateFields();
  }, [deliveryForm]);

  const cancelActualDeliveryEdit = useCallback(() => {
    setEditActualDeliveryVisible(false);
    actualForm?.resetFields();
    actualForm?.validateFields();
  }, [actualForm]);

  const showConfirmDropOffModal = useCallback(() => {
    setEditConfirmDropOffVisible(true);
  }, []);

  const handleClickConfirmPickup = useCallback(() => {
    const isOrderedExchange = adminShipmentData.dealerExchangeType === DealerExchangeType.Ordered;
    const shipmentHasUndeliveredParent =
      adminShipmentData.parentShipmentId && exchangeShipmentStatus < ShipmentStatus.Delivered;

    if (isOrderedExchange && shipmentHasUndeliveredParent) {
      setConfirmPickupDealerExchangePopConfirmVisible(true);
      return;
    }

    setEditConfirmPickupVisible(true);
  }, [adminShipmentData, exchangeShipmentStatus]);

  const cancelConfirmPickupEdit = useCallback(() => {
    setEditConfirmPickupVisible(false);
    confirmPickupForm?.resetFields();
    confirmPickupForm?.validateFields();
  }, [confirmPickupForm]);

  const cancelConfirmDropOffEdit = useCallback(() => {
    setEditConfirmDropOffVisible(false);
    confirmDropOffForm?.resetFields();
    confirmDropOffForm?.validateFields();
  }, [confirmDropOffForm]);

  const refreshData = useCallback(() => {
    const shipmentId = getUrlParams("shipmentId");

    dispatch({
      type: "adminShipmentView/fetchSingleAdminShipment",
      shipmentId,
    }).finally(() => {
      setEditPickupDeliveryVisible(false);
    });
  }, [dispatch]);

  if (!adminShipmentData) {
    return null;
  }

  const hasPickupOrDropOffDelivery =
    adminShipmentData?.load?.pickedUpAt != null || adminShipmentData?.load?.droppedOffAt != null;

  return (
    <Card
      title="Pickup/Delivery Times"
      style={{
        marginBottom: 16,
      }}
      bodyStyle={{ paddingTop: 8 }}
    >
      <div
        css={{
          display: "block",
          "@media(min-width: 767px)": {
            display: "grid",
            gridTemplateColumns: "49% 2% 49%",
          },
        }}
      >
        <div>
          <Descriptions
            column={1}
            title="Actual"
            extra={
              adminShipmentData?.load?.status >= LoadStatus.Active &&
              configPerm["CoreService.AdminShipments.ChangeDate"] &&
              hasPickupOrDropOffDelivery && (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={clickEditActualDelivery}
                  style={{
                    borderRadius: 10,
                    color: "#fff",
                    marginRight: "24px",
                  }}
                />
              )
            }
          >
            <Descriptions.Item key="pickup" label="Pickup Time">
              {dateStringToStandardFormat(adminShipmentData?.load?.pickedUpAt, { showTime: true }) || "Pending"}
              {adminShipmentData?.load?.status === LoadStatus.PickedUp && (
                <RemovePickupDropoffModal
                  loadId={adminShipmentData?.load?.id}
                  type={LocationType.Pickup}
                  onSuccess={refreshData}
                />
              )}
              {adminShipmentData?.load?.status === LoadStatus.Active &&
                configPerm["CoreService.AdminShipments.ConfirmLeg"] && (
                  <Popconfirm
                    title="First leg of exchange must be delivered first."
                    visible={confirmPickupDealerExchangePopConfirmVisible}
                    onConfirm={() => {
                      setConfirmPickupDealerExchangePopConfirmVisible(false);
                    }}
                    cancelButtonProps={{
                      style: { display: "none" },
                    }}
                  >
                    <Button
                      type="primary"
                      size="small"
                      style={{ marginLeft: "7%" }}
                      icon={<CheckOutlined />}
                      onClick={handleClickConfirmPickup}
                    >
                      Confirm Pickup
                    </Button>
                  </Popconfirm>
                )}
            </Descriptions.Item>
            <Descriptions.Item key="dropoff" label="Drop-off Time">
              {dateStringToStandardFormat(adminShipmentData?.load?.droppedOffAt) || "Pending"}
              {adminShipmentData?.load?.status === LoadStatus.Delivered && (
                <RemovePickupDropoffModal
                  loadId={adminShipmentData?.load?.id}
                  type={LocationType.Dropoff}
                  onSuccess={refreshData}
                />
              )}
              {configPerm["CoreService.AdminShipments.ConfirmLeg"] &&
                adminShipmentData?.load?.status === LoadStatus.PickedUp &&
                (adminShipmentData?.requiresLotExit && adminShipmentData?.lotExit?.pendingCount > 0 ? (
                  <Popconfirm
                    title="Lot exit must be confirmed first"
                    visible={lotExitPopConfirmVisible}
                    onConfirm={() => {
                      setLotExitPopConfirmVisible(false);
                    }}
                    cancelButtonProps={{
                      style: { display: "none" },
                    }}
                  >
                    <Button
                      type="primary"
                      size="small"
                      style={{ marginLeft: "5%" }}
                      icon={<CheckOutlined />}
                      onClick={() => {
                        setLotExitPopConfirmVisible(true);
                      }}
                    >
                      Confirm Drop-off
                    </Button>
                  </Popconfirm>
                ) : (
                  <Button
                    type="primary"
                    size="small"
                    style={{ marginLeft: "5%" }}
                    icon={<CheckOutlined />}
                    onClick={showConfirmDropOffModal}
                  >
                    Confirm Drop-off
                  </Button>
                ))}
            </Descriptions.Item>
          </Descriptions>
        </div>
        <Divider
          style={{
            height: "100%",
            borderLeft: "2px solid rgba(0, 0, 0, 0.1)",
            borderTop: "unset",
            margin: 0,
          }}
        />
        <div>
          <Descriptions
            column={1}
            title="ETA"
            extra={
              adminShipmentData.status === ShipmentStatus.Assigned &&
              configPerm["CoreService.AdminShipments.ChangeEta"] && (
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={clickEditPickupDelivery}
                  style={{
                    borderRadius: 10,
                    color: "#fff",
                  }}
                />
              )
            }
          >
            <Descriptions.Item key="pickupEta" label="Pickup ETA">
              <ETAWindow
                etaDate={adminShipmentData?.load?.pickupETA}
                etaTimeSlotIndex={adminShipmentData?.load?.pickupETATimeSlot}
              />
            </Descriptions.Item>
            <Descriptions.Item key="dropoffEta" label="Drop-off ETA">
              <ETAWindow
                etaDate={adminShipmentData?.load?.dropOffETA}
                etaTimeSlotIndex={adminShipmentData?.load?.dropOffETATimeSlot}
              />
            </Descriptions.Item>
          </Descriptions>
        </div>
      </div>

      <Modal
        title="Edit Pickup/Delivery"
        visible={editActualDeliveryVisible}
        onOk={actualForm.submit}
        onCancel={cancelActualDeliveryEdit}
        confirmLoading={isLoadingActualDelivery}
      >
        <ActualForm
          formRef={actualForm}
          shipmentData={adminShipmentData}
          setIsLoadingActualDelivery={setIsLoadingActualDelivery}
          refreshData={refreshData}
        />
      </Modal>

      <Modal
        title="Edit Pickup/Delivery ETAs"
        visible={editPickupDeliveryVisible}
        onOk={deliveryForm.submit}
        onCancel={cancelPickupDeliveryEdit}
        confirmLoading={isLoadingDelivery}
      >
        <ETAForm
          formRef={deliveryForm}
          shipmentData={adminShipmentData}
          setIsLoadingDelivery={setIsLoadingDelivery}
          refreshData={refreshData}
        />
      </Modal>

      <Modal
        title="Confirm Pickup Time"
        visible={editConfirmPickupVisible}
        onOk={confirmPickupForm.submit}
        onCancel={cancelConfirmPickupEdit}
        confirmLoading={isLoadingConfirmPickup}
      >
        <ConfirmPickupForm
          loadId={adminShipmentData?.load?.id}
          formRef={confirmPickupForm}
          pickupDate={adminShipmentData?.load?.pickedUpAt}
          setIsLoadingConfirmPickup={setIsLoadingConfirmPickup}
          refreshData={refreshData}
        />
      </Modal>

      <Modal
        title="Confirm Drop-off Time"
        visible={editConfirmDropOffVisible}
        onOk={confirmDropOffForm.submit}
        onCancel={cancelConfirmDropOffEdit}
        confirmLoading={isLoadingConfirmDropOff}
      >
        <ConfirmDropOffForm
          loadId={adminShipmentData?.load?.id}
          formRef={confirmDropOffForm}
          dropOffDate={adminShipmentData?.load?.droppedOffAt}
          setIsLoadingConfirmDropOff={setIsLoadingConfirmDropOff}
          refreshData={refreshData}
        />
      </Modal>
    </Card>
  );
};

export default connect(({ adminShipmentView, configs }) => ({
  adminShipmentView,
  vehicleAndLoadData: adminShipmentView.vehicleAndLoadData,
  configPerm: configs.configPerm,
}))(DeliveryTimes);
